/* eslint-disable react/display-name */
import React, { memo, useCallback } from 'react';
import styled from 'styled-components';

import { reduxConnect } from '../../../common';
import { selectHealthAdvice, selectPollutant } from '../../../common/actions/aqiTopTriggerHealthAdviceDetailsModalActions';
import { IStoreState } from '../../common/interfaces/types';

import { arrowDirections, ArrowIcon } from '../../common/utilities/arrowIcon';
import { HealthAdviceTypes, PollutantType } from './types';
import type { TopTriggerIcon } from './topTriggerIcons';

interface AirQualityListItemProps {
    itemIcon: React.ReactElement<React.ElementType<typeof TopTriggerIcon>>;
    itemTitle: string;
    pollutantConcentration?: string;
    showDivider: boolean;
    pollutantType?: keyof typeof PollutantType;
    healthAdviceType?: keyof typeof HealthAdviceTypes;
    selectPollutant?(showPopup: boolean, pollutantType: keyof typeof PollutantType): void;
    selectHealthAdvice?(showPopup: boolean, healthAdviceType: keyof typeof HealthAdviceTypes): void;

}

const showPopupOnClick = true;

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { appState } = storeState ?? {};
    const { user } = appState?.appState ?? {};
    const { Locality } = user?.ActiveLocation ?? {};
    const { Latitude, Longitude } = Locality ?? {};

    return {
        latitude: Latitude,
        longitude: Longitude
    };
};

const mapDispatchToProps = {
    selectPollutant,
    selectHealthAdvice
};

export const AirQualityListItem = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    pollutantType,
    healthAdviceType,
    selectPollutant,
    selectHealthAdvice, 
    itemIcon, 
    itemTitle, 
    pollutantConcentration,
    showDivider = true }: AirQualityListItemProps) => {

    const handleClick = useCallback(() => {
        if (pollutantType && !healthAdviceType && selectPollutant) {
            selectPollutant(showPopupOnClick, pollutantType);
        }
        if (healthAdviceType && !pollutantType && selectHealthAdvice) {
            selectHealthAdvice(showPopupOnClick, healthAdviceType);
        }
    }, [pollutantType, healthAdviceType, selectHealthAdvice, selectPollutant]);
    
    // Todo: 
    // Add proper error handling and loading UI instead of this once requirements from Product is available.
    if (!itemTitle) {
        return null;
    }

    return (
        <Wrapper onClick={handleClick}>
            <Container>
                <TitleIconContainer isHealthAdvice={!!healthAdviceType}>
                    <IconWrapper isHealthAdviceOrPollutantIcon={true}>
                        {itemIcon}
                    </IconWrapper>
                    <ItemTexts>
                        {itemTitle}
                    </ItemTexts>
                </TitleIconContainer>
                <LabelArrowIconContainer isHealthAdvice={!!healthAdviceType}>
                    {
                        pollutantConcentration 
                            && <ItemTexts isPollutantConcentration={true}>
                                {pollutantConcentration}
                            </ItemTexts>
                    }
                    <IconWrapper>
                        <ArrowIcon width={11.59} height={13} arrowDirection={arrowDirections.right} fillColor='#555555' />
                    </IconWrapper>
                </LabelArrowIconContainer>
            </Container>
            {showDivider && <ListItemDivider />}
        </Wrapper>
    );
}));

AirQualityListItem.displayName = 'AirQualityListItem';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px 8px;
    width: 100%;
    height: 48px;
    background: #F9F9F9;
    &:hover {
        cursor: pointer;
    }
`;

const Container = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0px 8px;
    width: 100%;
    height: calc(100% - 1px);
`;

interface TitleLabelProps {
    isHealthAdvice?: boolean;
}

const TitleIconContainer = styled.div<TitleLabelProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0px;
    width: ${({ isHealthAdvice }) => isHealthAdvice ? '65%' : '50%'};
    height: 24px;
`;

interface IconWrapperProps {
    isHealthAdviceOrPollutantIcon?: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${({ isHealthAdviceOrPollutantIcon }) => isHealthAdviceOrPollutantIcon ?  'flex-start' : 'flex-end'};
    width: 24px;
    height: 24px;
`;

interface ItemTextsProps {
    isPollutantConcentration?: boolean;
}

const ItemTexts = styled.span<ItemTextsProps>`
    display: flex;
    align-items: center;
    width: calc(100% - 38px);
    height: 20px;
    font-family: Source Sans Pro;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    margin-left: 13px;
    color: #555555;

    ${({ isPollutantConcentration }) => isPollutantConcentration && `
        justify-content: flex-end;
        margin-right: 7px;
        margin-left: 0px;
    `}
`;

const LabelArrowIconContainer = styled.div<TitleLabelProps>`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    padding: 0px;
    width: ${({ isHealthAdvice }) => isHealthAdvice ? '35%' : '50%'};
    height: 24px;
`;

const ListItemDivider = styled.span`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0px 8px;
    width: 100%;
    height: 1px;
    background: #EEEEEE;
`;
